import { useEffect, useId, useRef, useSyncExternalStore } from 'react';
import type { RefObject } from 'react';

// The overlay layer stack (CB-17; design M:8160-8199 layered ESC). Every overlay —
// popover/menu, sheet, drawer, full-screen editor — registers here while open, so:
//   · Esc and the browser Back button close the TOP layer only;
//   · page scroll is locked while any locking layer is open;
//   · focus returns to whatever was focused when the layer opened;
//   · Tab stays inside the top layer's container (a light focus trap).
// Toasts never register — they sit above everything and are not dismissable by Esc.
//
// The stack is a module singleton rather than React context, so an overlay works in
// any tree (tests render screens without extra providers) and nested overlays from
// different subtrees still share one order.
//
// Back button: when the first layer opens, ONE guard history entry is pushed (the
// current state is preserved so the router's own bookkeeping is untouched). A Back
// press pops the guard → the top layer closes, and the guard is re-pushed if layers
// remain. When the last layer closes by other means the guard is consumed with
// history.back() — deferred a microtask so a StrictMode unmount/remount does not
// bounce the history.

export type LayerKind = 'popover' | 'sheet' | 'drawer' | 'editor';

interface LayerEntry {
  id: string;
  kind: LayerKind;
  lockScroll: boolean;
  dismiss: () => void;
  container: RefObject<HTMLElement | null> | undefined;
  returnFocus: Element | null;
}

const GUARD_KEY = 'bdLayerGuard';

let stack: LayerEntry[] = [];
const listeners = new Set<() => void>();
let installed = false;
let ignoreNextPop = false;
let savedOverflow: string | null = null;

function emit() {
  for (const l of listeners) l();
}

function historyHasGuard(): boolean {
  try {
    return Boolean((window.history.state as Record<string, unknown> | null)?.[GUARD_KEY]);
  } catch {
    return false;
  }
}

function pushGuard() {
  if (historyHasGuard()) return;
  try {
    const prev = (window.history.state as Record<string, unknown> | null) ?? {};
    window.history.pushState({ ...prev, [GUARD_KEY]: true }, '');
  } catch {
    /* history unavailable (sandboxed frame) — Esc still works */
  }
}

function syncScrollLock() {
  if (typeof document === 'undefined') return;
  const lock = stack.some((l) => l.lockScroll);
  const body = document.body;
  if (lock && savedOverflow === null) {
    savedOverflow = body.style.overflow;
    body.style.overflow = 'hidden';
    body.dataset.layerLock = 'true';
  } else if (!lock && savedOverflow !== null) {
    body.style.overflow = savedOverflow;
    savedOverflow = null;
    delete body.dataset.layerLock;
  }
}

function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>(
      'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
    ),
  ).filter((el) => !el.hasAttribute('hidden') && el.getAttribute('aria-hidden') !== 'true');
}

function onKeyDown(e: KeyboardEvent) {
  const top = stack[stack.length - 1];
  if (!top) return;
  if (e.key === 'Escape') {
    e.preventDefault();
    e.stopPropagation();
    top.dismiss();
    return;
  }
  if (e.key === 'Tab') {
    const root = top.container?.current;
    if (!root) return;
    const items = focusables(root);
    if (items.length === 0) {
      e.preventDefault();
      root.focus();
      return;
    }
    const first = items[0]!;
    const last = items[items.length - 1]!;
    const active = document.activeElement;
    if (e.shiftKey && (active === first || !root.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !root.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  }
}

function onPopState() {
  if (ignoreNextPop) {
    ignoreNextPop = false;
    return;
  }
  const top = stack[stack.length - 1];
  if (!top) return;
  top.dismiss();
  // Still layered after the dismiss settles → keep a guard for the next Back.
  queueMicrotask(() => {
    if (stack.length > 0) pushGuard();
  });
}

function install() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  document.addEventListener('keydown', onKeyDown, true);
  window.addEventListener('popstate', onPopState);
}

function uninstall() {
  if (!installed) return;
  installed = false;
  document.removeEventListener('keydown', onKeyDown, true);
  window.removeEventListener('popstate', onPopState);
}

export function pushLayer(entry: LayerEntry) {
  install();
  const wasEmpty = stack.length === 0;
  stack = [...stack.filter((l) => l.id !== entry.id), entry];
  if (wasEmpty) pushGuard();
  syncScrollLock();
  emit();
}

export function removeLayer(id: string) {
  const entry = stack.find((l) => l.id === id);
  if (!entry) return;
  stack = stack.filter((l) => l.id !== id);
  syncScrollLock();
  emit();
  const target = entry.returnFocus;
  if (target instanceof HTMLElement && target.isConnected) target.focus();
  queueMicrotask(() => {
    if (stack.length > 0) return;
    uninstall();
    if (historyHasGuard()) {
      ignoreNextPop = true;
      try {
        window.history.back();
      } catch {
        ignoreNextPop = false;
      }
    }
  });
}

/** Test/diagnostic view of the open layers, bottom → top. */
export function openLayers(): readonly { id: string; kind: LayerKind }[] {
  return stack.map(({ id, kind }) => ({ id, kind }));
}

/** Test reset: drop every layer and listener without touching focus. */
export function __resetLayerStack() {
  stack = [];
  syncScrollLock();
  uninstall();
  ignoreNextPop = false;
  emit();
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/**
 * Register an overlay while `open`. `onDismiss` is what Esc / Back / the scrim call.
 * Returns whether this layer is currently the top one and its depth (0 = bottom),
 * so a primitive can stack its z-index or dim itself when covered.
 */
export function useLayer(
  open: boolean,
  opts: {
    kind: LayerKind;
    onDismiss: () => void;
    containerRef?: RefObject<HTMLElement | null>;
    lockScroll?: boolean;
  },
): { isTop: boolean; depth: number } {
  const id = useId();
  const dismissRef = useRef(opts.onDismiss);
  dismissRef.current = opts.onDismiss;
  const { kind, containerRef } = opts;
  const lockScroll = opts.lockScroll ?? kind !== 'popover';

  useEffect(() => {
    if (!open) return;
    pushLayer({
      id,
      kind,
      lockScroll,
      container: containerRef,
      dismiss: () => dismissRef.current(),
      returnFocus: typeof document !== 'undefined' ? document.activeElement : null,
    });
    // Move focus into the layer unless something inside already took it (autoFocus).
    const root = containerRef?.current;
    if (root && !root.contains(document.activeElement)) root.focus({ preventScroll: true });
    return () => removeLayer(id);
  }, [open, id, kind, lockScroll, containerRef]);

  const snapshot = useSyncExternalStore(
    subscribe,
    () => stack,
    () => stack,
  );
  const depth = snapshot.findIndex((l) => l.id === id);
  return { isTop: depth >= 0 && depth === snapshot.length - 1, depth };
}
